"use client";

import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { FiFilter } from "react-icons/fi";
import { timezones } from "../../utils/timezones.ts";
import { getBaseUrl, getUserToken } from "../../utils/helpers.js";
import { DASHBOARD_ASSETS } from "../../constants/dashboard.ts";

interface Filters {
  pair: string | null;
  timeframe: string | null;
  signalType: string | null;
  confidence?: number;
}

interface FilterProps {
  onFilterChange: (filters: Filters) => void;
  selectedTimeZone: any;
  setSelectedTimeZone: (value: any) => void;
  onTimeZoneChange?: (value: any) => void;
  availablePairs?: string[];
}

export function SimplifiedFilters({
  onFilterChange,
  selectedTimeZone,
  setSelectedTimeZone,
  onTimeZoneChange,
  availablePairs = [],
}: FilterProps) {
  const [selectedPair, setSelectedPair] = useState<string | null>("ALL");
  const [selectedSignalType, setSelectedSignalType] = useState<string | null>(null);
  const [confidence, setConfidence] = useState(0);
  const [pairOptions, setPairOptions] = useState<string[]>(["ALL", ...DASHBOARD_ASSETS]);

  const baseUrl = getBaseUrl();
  const token: string | undefined = getUserToken();

  useEffect(() => {
    if (availablePairs.length > 0) {
      setPairOptions(["ALL", ...Array.from(new Set([...availablePairs.filter(Boolean), ...DASHBOARD_ASSETS]))]);
      return;
    }

    if (!baseUrl || !token) {
      setPairOptions(["ALL", ...DASHBOARD_ASSETS]);
      return;
    }

    fetch(`${baseUrl}/api/pairs`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const results = Array.isArray(data?.results) ? data.results : [];
        const options = results
          .sort((a, b) => Number(a.sort || 0) - Number(b.sort || 0))
          .map((item) => item.pair_name || item.asset || item.pair)
          .filter(Boolean);

        setPairOptions(["ALL", ...Array.from(new Set([...options, ...DASHBOARD_ASSETS]))]);
      })
      .catch(() => setPairOptions(["ALL", ...DASHBOARD_ASSETS]));
  }, [availablePairs, baseUrl, token]);

  useEffect(() => {
    onFilterChange({
      pair: selectedPair,
      timeframe: null,
      signalType: selectedSignalType,
      confidence,
    });
  }, [selectedPair, selectedSignalType, confidence]);

  const timezoneOptions = useMemo(() => timezones.slice(0, 24), []);

  const handleTimezoneChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const id = String(e.target.value);
    const tz = timezones.find((item) => item.id === id);
    if (!tz) return;
    if (onTimeZoneChange) onTimeZoneChange(tz);
    else setSelectedTimeZone(tz);
  };

  if (!selectedTimeZone) return null;

  const selectClass =
    "h-10 rounded-xl border border-[#dfe8e1] bg-[#f4f7f1] px-3 text-sm font-semibold text-[#153329] outline-none transition focus:border-[#20c77a] focus:bg-white focus:ring-4 focus:ring-[#20c77a]/10";

  return (
    <section className="rounded-[20px] border border-[#dfe8e1] bg-white p-4 shadow-[0_16px_40px_rgba(20,44,33,0.07)]">
      <div className="mb-3 flex items-center gap-2 text-[#0d2a1f]">
        <FiFilter className="h-5 w-5" />
        <h2 className="text-base font-black">Filtros</h2>
      </div>

      <div className="grid items-center gap-3 md:grid-cols-2 lg:grid-cols-[1.2fr_1fr_0.95fr_1.15fr]">
        <select
          className={selectClass}
          value={selectedPair || "ALL"}
          onChange={(e) => setSelectedPair(e.target.value)}
        >
          {pairOptions.map((pair) => (
            <option key={pair} value={pair}>
              {pair === "ALL" ? "Todos os ativos" : pair}
            </option>
          ))}
        </select>

        <select
          className={selectClass}
          value={selectedSignalType || "ALL"}
          onChange={(e) => setSelectedSignalType(e.target.value === "ALL" ? null : e.target.value)}
        >
          <option value="ALL">Direcao: todas</option>
          <option value="CALL">CALL</option>
          <option value="PUT">PUT</option>
        </select>

        <select
          className={`${selectClass} min-w-0`}
          value={selectedTimeZone.id}
          onChange={handleTimezoneChange}
          aria-label="Timezone"
        >
          {timezoneOptions.map((tz) => (
            <option key={tz.id} value={tz.id}>
              {tz.name.replace("GMT", "UTC")}
            </option>
          ))}
        </select>

        <div className="flex h-10 flex-col justify-center rounded-xl bg-white px-1">
          <div className="mb-1 flex items-center justify-between text-xs font-semibold text-[#73827b]">
            <span>Confianca min.</span>
            <span>{confidence}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            value={confidence}
            onChange={(e) => setConfidence(Number(e.target.value))}
            className="accent-[#20c77a]"
          />
        </div>
      </div>
    </section>
  );
}
